import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { UserData } from "@/common/types/users";
import { defaultColumns } from "@/features/companies/constants/companiesColumns.constant";
import { useCompanies } from "@/features/companies/hooks/useCompanies";
import { CompaniesTransformer } from "@/features/companies/services/companiesTransformer";
import { CompanyData, CompanyTableItem } from "@/features/companies/types/companies.type";
import { options as expectedPaymentModalityOptions } from "@/features/privateCompanyLeadsFunnel/constants/expectedPaymentModalityOptions.constant";
import { useUsers } from "@/features/users/hooks/useUsers";
import { BriefcaseBusinessIcon, SearchIcon } from "lucide-react";
import { options as kindOptions } from "../../constants/kind.constant";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import MaskedTextField from "@/common/components/MaskedTextField";
import { currencyMask } from "@/common/constants/masks.constant";
import { companyStatusOptions } from "../../constants/companyStatus";
import { useCardsTotalizer } from "@/features/cards/hooks/useCardsTotalizer";
import { CardsTotalizerData } from "@/features/cards/types/cardsTotalizer.type";
import { useState } from "react";

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [maximumConsumption, setMaximumConsumption] = useDebounce(filterParams.maximumConsumption || '');
  const [searchUser, setSearchUser] = useDebounce('');
  const [companyIdToSeeCardsInfo, setCompanyIdToSeeCardsInfo] = useState<string>()

  const { data: cardsTotalizer, isRefetching: isPendingCards } = useCardsTotalizer<CardsTotalizerData>({
    filters: {
      company_id: companyIdToSeeCardsInfo,
    },
  });

  const { data: companies, meta, isLoading: isLoadingCompanies } = useCompanies({
    filters: {
      page: filterParams.page || 1,
      search: search,
      status: filterParams.status?.value,
      kind: filterParams.kind?.value,
      paymentModality: filterParams.paymentModality?.value,
      commercialManagerId: filterParams.user?.value,
      maximumConsumption: maximumConsumption
    },
    mapper: Mapper.mapWithTransformer<CompanyData[], CompanyTableItem[]>({
      transformer: CompaniesTransformer.tableTransformer(setCompanyIdToSeeCardsInfo, cardsTotalizer, isPendingCards)
    })
  });

  const { data: users } = useUsers({
    filters: { search: searchUser },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' })
  });

  return (
    <div>
      <div className="flex flex-col gap-6">
        <ListHeader
          Icon={<BriefcaseBusinessIcon className="w-10 h-10" strokeWidth={1.5} />}
          title="Empresas e Filiais"
        />

        <div className="h-[calc((100vh-40px-4rem-1.5rem))] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
              }}
              value={filterParams.search}
            />
            <div className="flex items-center gap-2">
              <ListFilters
                filters={[
                  {
                    className: '',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                    value: filterParams.status,
                    options: [{ label: 'Todas', value: '' }, ...(companyStatusOptions || [])],
                    placeholder: 'Status'
                  },
                  {
                    className: 'w-44',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, kind: e })),
                    value: filterParams.kind,
                    options: [{ label: 'Todas', value: '' }, ...(kindOptions || [])],
                    placeholder: 'Tipo de empresa'
                  },
                  {
                    className: '',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, paymentModality: e })),
                    value: filterParams.paymentModality,
                    options: [{ label: 'Todas', value: '' }, ...(expectedPaymentModalityOptions || [])],
                    placeholder: 'Modalidade de pagamento'
                  },
                  {
                    className: '',
                    onInputChange: (inputValue) => setSearchUser(inputValue),
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, user: e })),
                    value: filterParams.user,
                    options: [{ label: 'Todos', value: '' }, ...users || []],
                    placeholder: 'Responsável'
                  },
                ]}
              />
              <MaskedTextField
                placeholder="Máximo Consumido"
                mask={currencyMask(1000000000, false)}
                variant='filter'
                name="max_consumption_filter"
                value={filterParams.maximumConsumption}
                onChange={(e) => {
                  setMaximumConsumption((e.target as HTMLInputElement).value)
                  setFilterParams((prev) => ({ ...prev, maximumConsumption: e.target.value }))
                }}
              />
            </div>
          </div>

          <Table
            columns={defaultColumns}
            isLoadingData={isLoadingCompanies}
            page={filterParams.page || 1}
            rows={companies}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </div>
    </div>
  );
};

export default List;
